import React, {useState, useEffect, useRef} from "react";
import {createPortal} from "react-dom";
import ModalHeader from "@/customComponents/uiComponents/modal/ModalHeader.jsx";
import {InterestFreePaymentContent} from "@/customComponents/BookRideSteps/modalPages/interestFreePayment.jsx";
import {FlightDelayedContent} from "@/customComponents/BookRideSteps/Components/flightDelayedContent.jsx";
import {PayAfterDropoffContent} from "@/customComponents/BookRideSteps/modalPages/payAfterDropoff.jsx";
import {CancellationModificationRefundContent} from "@/customComponents/BookRideSteps/Components/cancellationModificationRefund.jsx";
import {PrebookCompareDetail} from "@/customComponents/BookRideSteps/Components/prebookCompare.jsx";
import WhyChooseUs from "@/customComponents/sharedComponents/WhyChooseUs.jsx";
import ExclusivePerksBar from "@/customComponents/sharedComponents/ExclusivePerksBar.jsx";
import PopularQuestions, {FaqAccordionList} from "@/customComponents/sharedComponents/PopularQuestions.jsx";
import {FAQ_INLINE_COUNT} from "@/constants/faqs.js";
import {img} from "@/utils/pluginImg.js";

// Shared booking perk/FAQ/compare sections — the SAME UI selectedCarDetailsLatest
// renders, reused in the bookingReceived "Airport Perks & FAQs" tab. Each component
// is self-contained: it manages its own popup state and (on mobile) routes to the
// parallel step via `setStep`.

/**
 * Overlay host for these sections' popups.
 *
 * Normally (selectedCarDetailsLatest) the `.upper-modal` renders in place — it's
 * absolute, so it fills the nearest positioned ancestor and everything is fine.
 *
 * On bookingReceived the sections live inside a Swiper slide, whose transform is
 * the containing block for anything absolute/fixed below it: the overlay ends up
 * trapped in the card, sized and clipped to it. `portal` lifts it out to <body>
 * so it becomes a real, viewport-centred modal — `.hopn-br-popup` carries the
 * fixed positioning + width cap.
 */
function PopupHost({portal, children}) {
    if (!portal) return children;
    // No document during SSR / before mount — render in place rather than crash.
    return typeof document !== 'undefined' ? createPortal(children, document.body) : children;
}

// Overlay class: tagged for the portalled (real modal) variant.
const overlayClass = (portal) => `upper-modal${portal ? ' hopn-br-popup' : ''}`;

const WHY_PERK_VIEWS = {
    afterpay: {title: "Ride now Pay later", Content: InterestFreePaymentContent},
    flight: {title: "Flight tracking", Content: FlightDelayedContent},
    payafter: {title: "", Content: PayAfterDropoffContent},
    cancellation: {title: "", Content: CancellationModificationRefundContent},
};

// The nested Prebook-vs-Ride-Sharing modal (compare → "More"/Why-Choose-Us → perk
// detail). Self-contained: opens at `initialView`, manages its own view transitions.
function CompareModal({open, initialView, onClose, pickup, dropoff, price, sharePrice, portal = false}) {
    const [view, setView] = useState(initialView);
    const contentRef = useRef(null);
    useEffect(() => { if (open) setView(initialView); }, [open, initialView]);
    useEffect(() => { if (open) contentRef.current?.scrollTo(0, 0); }, [view, open]);
    if (!open) return null;
    const perkView = WHY_PERK_VIEWS[view];
    const PerkContent = perkView?.Content;
    return (
        <PopupHost portal={portal}>
        <div className={overlayClass(portal)} style={{display: 'flex'}}>
            <div className="upper-modal-inner">
                <ModalHeader
                    inline={true}
                    title={perkView?.title || ""}
                    extraClasses="less-space"
                    totalSteps={0}
                    cancelButton={true}
                    cancelButtonType="icon"
                    backStep={view === 'compare' ? null : "back"}
                    showBackButton={view !== 'compare'}
                    setStep={() => setView(perkView ? 'more' : 'compare')}
                    closeModal={onClose}
                />
                <div ref={contentRef} className={`upper-modal-content ${view !== 'compare' ? 'padding-bottom-30' : ''}`}>
                    <div className="modalPages-spacing">
                        {perkView
                            ? <PerkContent/>
                            : view === 'more'
                                ? <WhyChooseUs onPerkAction={setView}/>
                                : <PrebookCompareDetail pickup={pickup} dropoff={dropoff} price={price} sharePrice={sharePrice}/>}
                    </div>
                </div>
                {view === 'compare' && (
                    <div className="hopn-modal-footer prebook-compare">
                        <ExclusivePerksBar onClick={() => setView('more')}/>
                    </div>
                )}
            </div>
        </div>
        </PopupHost>
    );
}

// "Exclusive Airport Pickup Perks" bar — opens the "Why Choose us?" view
// (desktop: 'more' view of the compare popup; mobile: dedicated step).
export function AirportPerksBar({price, pickup, dropoff, sharePrice, setStep, spacing = true, forcePopup = false, portal = false, compact = false}) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <div className={`${spacing ? 'modalPages-spacing ' : ''}prebook-compare hopn-ride-perks-bar${compact ? ' is-compact' : ''}`}>
                <ExclusivePerksBar compact={compact}
                                   onClick={() => (forcePopup || window.innerWidth >= 576) ? setOpen(true) : setStep('whyChooseUs')}/>
            </div>
            <CompareModal open={open} initialView="more" onClose={() => setOpen(false)} portal={portal}
                          pickup={pickup} dropoff={dropoff} price={price} sharePrice={sharePrice}/>
        </>
    );
}

// Prebook vs Ride-Sharing compare card. Renders only with a real price.
export function PrebookCompareCard({price, pickup, dropoff, sharePrice, setStep, spacing = true, forcePopup = false, portal = false}) {
    const [open, setOpen] = useState(false);
    if (!price) return null;
    return (
        <>
            <div className={spacing ? 'modalPages-spacing' : ''}>
                <div className="hopn-ride-compare">
                    <div className="hopn-ride-compare-row">
                        <div className="hopn-ride-compare-head">
                            <small>{pickup} <img src={img("arrow-right.svg")} alt=""/> {dropoff}</small>
                        </div>
                        <div className="hopn-ride-compare-row-section">
                            <div className="hopn-ride-compare-side hopn">
                                <img src={img("compare-hopn-car.svg")} alt="" width="150"/>
                                <div className="hopn-ride-compare-content">
                                    <img src={img("logo-latest.svg")} alt="" width="64"/>
                                    <span className="amt">${price}</span>
                                    <small className="muted">BLACK · SUV · EXECUTIVE</small>
                                </div>
                            </div>
                            <span className="hopn-ride-compare-vs">VS</span>
                            <div className="hopn-ride-compare-side share">
                                <img src={img("compare-share-car.svg")} alt="Ride-share" width="128"/>
                                <div className="hopn-ride-compare-content">
                                    <small>Ride-share (est.)</small>
                                    <span className="amt">~${sharePrice}</span>
                                    <small className="muted">May Vary With Surge</small>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="full-view-button">
                        <button
                            type="button"
                            className="hopn-ride-compare-toggle"
                            onClick={() => (forcePopup || window.innerWidth >= 576) ? setOpen(true) : setStep('prebookCompare')}
                            aria-label="Open comparison details"
                        >
                            Prebook vs Ride-Sharing
                            <img src={img("arrow-long-down.svg")} alt=""/>
                        </button>
                    </div>
                </div>
            </div>
            <CompareModal open={open} initialView="compare" onClose={() => setOpen(false)} portal={portal}
                          pickup={pickup} dropoff={dropoff} price={price} sharePrice={sharePrice}/>
        </>
    );
}

// "Popular questions" — reuses the shared PopularQuestions (inline list + head) and
// adds the "More" popup (desktop) / step (mobile) for the rest of the list.
export function PopularQuestionsSection({faqs, setStep, spacing = true, forcePopup = false, portal = false, title, chip = ''}) {
    const [faqModal, setFaqModal] = useState(false);
    return (
        <>
            {faqModal && (
                <PopupHost portal={portal}>
                <div className={overlayClass(portal)} style={{display: 'flex'}}>
                    <div className="upper-modal-inner">
                        <ModalHeader
                            inline={true} title="Popular questions" extraClasses="less-space" totalSteps={0}
                            cancelButton={true} cancelButtonType="icon" backStep={null} showBackButton={false}
                            setStep={setStep} closeModal={() => setFaqModal(false)}
                        />
                        <div className="upper-modal-content padding-bottom-30">
                            <div className="hopn-ride-latest">
                                <div className="modalPages-spacing">
                                    <div className="hopn-ride-faq">
                                        <FaqAccordionList items={faqs.slice(FAQ_INLINE_COUNT)}/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                </PopupHost>
            )}
            <div className={spacing ? 'modalPages-spacing' : ''}>
                <PopularQuestions faqs={faqs} title={title} chip={chip}
                                  onMore={() => (forcePopup || window.innerWidth >= 576) ? setFaqModal(true) : setStep('popularQuestions')}/>
            </div>
        </>
    );
}
